// Components
import { Breadcrumbs, Button } from '@brewlogix/blx-ui'
import { CompositeProduct, InputGetBySlug } from 'src/types/schema/graphql'
import { GetServerSideProps, NextPage } from 'next'
import { IDynamicData } from 'src/features/seo/types'
import Link from 'next/link'
import SEOTags from 'src/features/seo/components/SEOTags'
import StructuredData from 'src/features/seo/components/StructuredData'
import { parseProductMeta } from 'src/features/seo/utils/helpers/parseProductMeta'
import { useEffect } from 'react'
import { getProductBySlug } from 'src/features/brewer/utils/graph/getProductBySlug'
import BrewDetail from 'src/features/BrewDetail/components/BrewDetail'
import { useBrewDetailStore } from 'src/features/BrewDetail/state/useBrewDetailStore'
import { apiRequest, RequestInput } from 'src/api/apiRequest'

interface ISingleProductPage {
    product: CompositeProduct
    seoData: IDynamicData
}

const Page: NextPage<ISingleProductPage> = ({ product, seoData }) => {
    const { setProductData } = useBrewDetailStore()
    useEffect(() => {
        setProductData(product)
    }, [])

    return (
        <div className='bdb-product'>
            <SEOTags schemaType='product' dynamicData={seoData} />
            <StructuredData schemaType='product' dynamicData={seoData} />
            <div className='hidden bg-gray-50 py-3 md:block'>
                <Breadcrumbs
                    customClasses='px-24'
                    omitRootLabel={false}
                    customBreadcrumbs={[
                        {
                            breadcrumb:
                                product?.brand &&
                                (product?.brand.displayTitle || product?.brand.title),
                            href: `/brewer/${(product?.brand && product?.brand.slug) || '...'}`,
                        },
                        {
                            breadcrumb: encodeURIComponent(product?.title),
                            href: `/brewer/${
                                (product?.brand && product?.brand.slug) || '...'
                            }/products/${product?.slug}`,
                        },
                    ]}
                />
            </div>
            <section className='bg-white'>
                <BrewDetail />
            </section>
            <section className='bg-gray-50 py-6'>
                <div className='container mx-auto flex flex-row items-center justify-center text-center'>
                    <Link href={`/brewer/${product?.brand && product?.brand.slug}#brews`}>
                        <a className=''>
                            <Button
                                icon='HeroIcons.ChevronLeft'
                                iconAlignment='left'
                                theme='link'
                                size='lg'
                                label={`Back to ${product?.brand && product?.brand.title}`}
                                customClasses='mx-0 text-dark hover:text-info'
                            />
                        </a>
                    </Link>
                </div>
            </section>
        </div>
    )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
    const { params } = context
    let product = {}
    let seoData = {} as IDynamicData
    const input: RequestInput<InputGetBySlug> = {
        query: getProductBySlug,
        args: {
            input: {
                parentSlug: String(params.slug),
                itemSlug: String(params?.productSlug),
            },
        },
        file: 'pages/brewer/[slug]/products/[productSlug].ts',
        endpoint: 'getProductBySlug',
        noAlerts: true,
        publicQuery: true,
    }
    const { data, errors } = await apiRequest<CompositeProduct, InputGetBySlug>(input)
    if (data) {
        product = data
        seoData = parseProductMeta(data)
    }

    if (errors) {
        return {
            notFound: true,
        }
    }
    return {
        props: { product, seoData },
    }
}

export default Page
